import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { trackLead } from "@/lib/analyticsEvents";
import { useSite } from "@/contexts/SiteContext";
import { ChevronRight } from "lucide-react";
import ContactChannels from "@/components/ContactChannels";

export default function ContactForm({ embedded = true }) {
  const { sectionContent } = useSite();
  const location = useLocation();
  const c = sectionContent("contact") || { title: "Programează o discuție", subtitle: "" };
  const [form, setForm] = useState({
    name: "", whatsapp: "", email: "", topic: "", worked_before: false, consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Pre-fill topic from URL params (e.g. when navigated from CourseDetail "Întreabă")
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const subiect = params.get("subiect");
    const interes = params.get("interes");
    if (subiect || interes) {
      setForm((f) => ({
        ...f,
        topic: subiect ? `${subiect}\n\n` : f.topic,
        interest: interes || "",
      }));
    }
  }, [location.search]);

  const onChange = (k) => (e) => {
    const val = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) {
      toast.error("Trebuie să accepți politica de confidențialitate.");
      return;
    }
    setSuccess(false);
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/contact", form);
      setSuccess(true);
      toast.success(data.message || "Trimis cu succes.");
      trackLead("contact");
      setForm({ name: "", whatsapp: "", email: "", topic: "", worked_before: false, consent: false, interest: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const inner = (
    <form
      data-testid="contact-form"
      onSubmit={submit}
      className="space-y-4 card-premium p-7 md:p-9"
    >
      {!embedded && (
        <div>
          <div className="flex items-center gap-3 mb-3">
            <span className="gold-rule" /> <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">{c.eyebrow || "Programare"}</span>
          </div>
          <h2 data-el="title" data-field="title" className="font-serif text-3xl sm:text-4xl">{c.title}</h2>
          {c.subtitle && <p data-el="subtitle" data-field="subtitle" className="text-sm mt-2" style={{ color: "var(--ct-text-muted)" }}>{c.subtitle}</p>}
          {c.show_channels && (
            <div className="mt-5" data-el="contact_channels" data-testid="contact-form-channels-standalone">
              <ContactChannels variant="row" />
            </div>
          )}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label htmlFor="contact-name" className="sr-only">Nume</label>
          <input id="contact-name" data-testid="contact-name" required autoComplete="name" placeholder="Nume" className="premium-input" value={form.name} onChange={onChange("name")} />
        </div>
        <div>
          <label htmlFor="contact-whatsapp" className="sr-only">WhatsApp / Telefon</label>
          <input id="contact-whatsapp" data-testid="contact-whatsapp" required type="tel" autoComplete="tel" placeholder="WhatsApp / Telefon" className="premium-input" value={form.whatsapp} onChange={onChange("whatsapp")} />
        </div>
      </div>
      <label htmlFor="contact-email" className="sr-only">Email</label>
      <input id="contact-email" data-testid="contact-email" type="email" required autoComplete="email" placeholder="Email" className="premium-input" value={form.email} onChange={onChange("email")} />
      <label htmlFor="contact-topic" className="sr-only">Ce vrei să lucrezi?</label>
      <textarea
        id="contact-topic"
        data-testid="contact-topic"
        required
        rows={4}
        placeholder="Ce vrei să lucrezi?"
        className="premium-input resize-none"
        value={form.topic}
        onChange={onChange("topic")}
      />
      <label className="flex items-center gap-3 text-sm cursor-pointer" style={{ color: "var(--ct-text-muted)" }}>
        <input data-testid="contact-worked-before" type="checkbox" checked={form.worked_before} onChange={onChange("worked_before")} className="accent-[var(--ct-gold)]" />
        Am mai lucrat cu Georgiana
      </label>
      <label className="flex items-start gap-3 text-xs cursor-pointer" style={{ color: "var(--ct-text-muted)" }}>
        <input data-testid="contact-consent" type="checkbox" checked={form.consent} onChange={onChange("consent")} className="mt-1 accent-[var(--ct-gold)]" />
        Sunt de acord cu prelucrarea datelor mele personale conform Politicii de confidențialitate (GDPR).
      </label>
      <button type="submit" disabled={submitting} className="btn-primary w-full justify-center" data-testid="contact-submit">
        {submitting ? "Se trimite..." : "Trimite cererea"}
        <ChevronRight size={16} />
      </button>
      {success && (
        <p className="text-sm text-center" style={{ color: "var(--ct-link)" }}>
          Mulțumesc! Revin în maxim 24 de ore.
        </p>
      )}
    </form>
  );

  if (!embedded) return inner;
  return (
    <section data-testid="contact-section" className="py-24 lg:py-32" style={{ background: "var(--ct-surface-2)" }}>
      <div className="max-w-5xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="flex items-center gap-4 mb-3">
            <span className="gold-rule" /> <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">{c.eyebrow || "Programare"}</span>
          </div>
          <h2 data-el="title" data-field="title" className="font-serif text-4xl sm:text-5xl leading-tight">{c.title}</h2>
          {c.subtitle && <p data-el="subtitle" data-field="subtitle" className="text-base leading-relaxed mt-4" style={{ color: "var(--ct-text-muted)" }}>{c.subtitle}</p>}
          <ul className="mt-8 space-y-3 text-sm" style={{ color: "var(--ct-text-muted)" }}>
            <li className="flex items-start gap-2"><span style={{ color: "var(--ct-gold)" }}>—</span> <span data-field="bullet_1">{c.bullet_1 || "Răspuns în maxim 24h pe WhatsApp."}</span></li>
            <li className="flex items-start gap-2"><span style={{ color: "var(--ct-gold)" }}>—</span> <span data-field="bullet_2">{c.bullet_2 || "Discuție inițială pentru a alege cea mai potrivită formă de lucru."}</span></li>
            <li className="flex items-start gap-2"><span style={{ color: "var(--ct-gold)" }}>—</span> <span data-field="bullet_3">{c.bullet_3 || "Confidențialitate totală."}</span></li>
          </ul>
          {c.show_channels && (
            <div className="mt-7" data-el="contact_channels" data-testid="contact-form-channels">
              <ContactChannels variant="row" />
            </div>
          )}
        </div>
        {inner}
      </div>
    </section>
  );
}
